import { cn } from "@/lib/utils";
import { useState, useEffect, useRef } from "react";

/**
 * LivePreview Component
 * A reusable sticky preview panel implementing Figma-style sticky positioning
 *
 * Figma Sticky Behavior:
 * - Element scrolls with content initially (relative positioning)
 * - Once it reaches the threshold (top edge), it becomes fixed (sticky)
 * - Remains visible as user continues scrolling
 * - Unsticks and moves down when scrolling back up
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to display in the preview
 * @param {string} props.title - Title for the preview header
 * @param {string} props.description - Description text for the preview header
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.showOnMobile - Whether to show on mobile (default: false)
 */
export default function LivePreview({
  children,
  title = "Live Preview",
  description = "This is how your content will appear on the website.",
  className,
  showOnMobile = false,
}) {
  const [isStuck, setIsStuck] = useState(false);
  const [_initialOffset, setInitialOffset] = useState(0);
  const stickyRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    // Get initial position when component mounts
    if (stickyRef.current && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const stickyRect = stickyRef.current.getBoundingClientRect();
      // Calculate initial offset from top of viewport
      setInitialOffset(stickyRect.top - containerRect.top);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!stickyRef.current) return;

      // Get bounding rectangle to detect sticky state
      const stickyRect = stickyRef.current.getBoundingClientRect();

      // Sticky behavior: element sticks when its top reaches the threshold
      // The sticky threshold matches the CSS `top-20` value (5rem = 80px)
      const stickyThreshold = 80; // top-20 = 5rem = 80px

      // Check if element has reached its sticky position
      // When the element's top edge reaches or goes below the threshold, it becomes "stuck"
      const currentTop = stickyRect.top;
      const isCurrentlyStuck = currentTop <= stickyThreshold;

      setIsStuck(isCurrentlyStuck);
    };

    // Add scroll event listener with passive option for better performance
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Check initial state
    handleScroll();

    // Cleanup
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <aside
      ref={containerRef}
      className={cn(
        // Base styling - container for sticky element (matches Terms.jsx pattern)
        "shrink-0",
        // Fill available width and height
        "h-full w-full",
        // Hide on mobile by default, show on large+ screens where grid activates
        !showOnMobile && "hidden lg:block",
        // Show on all screens if showOnMobile is true
        showOnMobile && "block",
        className
      )}
      aria-label="Live Preview">
      {/* Sticky wrapper - Working pattern from Terms.jsx */}
      {/* CRITICAL: self-start must be on the sticky element itself, not the parent */}
      <div
        ref={stickyRef}
        className={cn(
          // Sticky positioning (matches Terms.jsx working pattern)
          "sticky",
          // Sticky threshold: Adjusted to stick a bit lower from the top
          // This gives more breathing room below the admin header (h-16 = 64px)
          // The header is z-50, so we stick at top-20 (80px) with comfortable spacing
          "top-20",
          // CRITICAL: self-start on the sticky element (like Terms.jsx)
          // This prevents the sticky element from stretching in the grid
          "self-start",
          // Ensure proper positioning and alignment
          "h-fit w-full",
          // Ensure z-index is above other content (below header which is z-50)
          "z-10",
          // Smooth transition when switching between relative and sticky states
          "transition-all duration-300 ease-out"
        )}
        style={{
          // Optimize for smooth scrolling performance
          willChange: "transform",
          backfaceVisibility: "hidden",
          WebkitBackfaceVisibility: "hidden",
        }}>
        <div
          className={cn(
            // Base card styling - matches Figma example appearance
            "rounded-lg border bg-white",
            // Smooth transition for visual feedback (Figma-like smooth animations)
            "transition-all duration-300 ease-out",
            // Figma-style visual feedback: enhanced appearance when stuck
            // When stuck, the element should feel more "elevated" and prominent
            isStuck
              ? // When stuck (Figma sticky state): more prominent, elevated appearance
                // Enhanced shadow and border to indicate it's in "sticky" state
                "border-gray-400 shadow-xl ring-1 shadow-gray-300/60 ring-gray-200"
              : // When not stuck (scrolling normally): subtle styling
                // Normal appearance while scrolling with content
                "border-gray-200 shadow-sm"
          )}>
          {/* Header - Visual feedback when stuck (Figma-style) */}
          <div
            className={cn(
              "border-b px-4",
              // Smooth transition matching Figma animations
              "transition-all duration-300 ease-out",
              // Figma-style header changes when stuck
              isStuck
                ? // When stuck: slightly more compact, enhanced background
                  // Creates a more "elevated" feeling when in sticky state
                  "border-gray-300 bg-gray-100/80 py-2.5 backdrop-blur-sm"
                : // When not stuck: normal padding
                  "border-gray-200 bg-gray-50 py-3"
            )}>
            <h2
              className={cn(
                "text-sm font-semibold",
                // Smooth transition
                "transition-all duration-300 ease-out",
                // Visual feedback when stuck
                isStuck ? "font-bold text-gray-900" : "text-gray-800"
              )}>
              {title}
            </h2>
            <p
              className={cn(
                "mt-1 text-xs",
                // Smooth transition
                "transition-all duration-300 ease-out",
                // Visual feedback when stuck
                isStuck ? "text-gray-700" : "text-gray-500"
              )}>
              {description}
            </p>
          </div>

          {/* Content */}
          {/* Dynamic max-height calculation:
              - 100vh (full viewport)
              - minus 5rem (top-20 sticky position = 80px)
              - minus 8rem (header + padding + bottom margin)
              = calc(100vh - 13rem) ensures no overflow at page bottom
          */}
          <div className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent max-h-[calc(100vh-13rem)] space-y-3 overflow-x-hidden overflow-y-auto p-4">
            {children}
          </div>
        </div>
      </div>
    </aside>
  );
}
